import React, {useEffect, useMemo, useState} from "react"

import PropTypes from "prop-types"
import {Grid, Icon, Accordion as SemanticAccordion} from "semantic-ui-react"

import colors from "../../styles/_default.colors.module.scss"
import {isKeyboardClickEvent} from "../../utils/accessibilityUtil"
import {getDataTestId} from "../../utils/stringUtil"

import "./accordion.scss"

const Accordion = ({
    borderless = false,
    children,
    fixed = false,
    headerBackgroundColor = colors.darkBlue50,
    onClick,
    openedOnMount = false,
    rightComponent = null,
    sticked = false,
    ...rest
}) => {
    const [activePanel, setActivePanel] = useState()

    useEffect(() => {
        if (openedOnMount) {
            setActivePanel(0)
        }
    }, [openedOnMount])

    const panels = useMemo(
        () =>
            React.Children.map(React.Children.toArray(children).filter(Boolean), (child, index) => {
                const handleClick = (_, titleProps) => {
                    if (!fixed) {
                        const {index} = titleProps
                        const newIndex = activePanel === index ? -1 : index
                        setActivePanel(newIndex)

                        if (!!onClick) {
                            onClick()
                        }
                    }
                }

                const handleKeyDown = event => {
                    if (isKeyboardClickEvent(event)) {
                        event.preventDefault()
                        handleClick(event, {index})
                    }
                }

                const isExpanded = activePanel === index
                const panelId = `accordion-panel-${index}`
                const buttonId = `accordion-button-${index}`

                return (
                    <Grid className={sticked ? "sticked" : ""} columns={1}>
                        <Grid.Column>
                            <SemanticAccordion className={borderless ? "borderless" : ""} {...rest} styled>
                                <SemanticAccordion.Title
                                    active={isExpanded}
                                    aria-controls={panelId}
                                    aria-expanded={isExpanded}
                                    data-testid={getDataTestId(child.props.title, "accordeon")}
                                    id={buttonId}
                                    index={index}
                                    onClick={handleClick}
                                    onKeyDown={handleKeyDown}
                                    style={{backgroundColor: headerBackgroundColor}}
                                    tabIndex={0}
                                >
                                    {!fixed && <Icon aria-hidden="true" className="fa-solid fa-caret-down" />}
                                    <span>{child.props.title}</span>
                                    {rightComponent && <div className="right-component">{rightComponent}</div>}
                                </SemanticAccordion.Title>
                                <SemanticAccordion.Content active={isExpanded} id={panelId}>
                                    {child}
                                </SemanticAccordion.Content>
                            </SemanticAccordion>
                        </Grid.Column>
                    </Grid>
                )
            }),
        [children, sticked, borderless, rest, activePanel, headerBackgroundColor, fixed, onClick, rightComponent],
    )

    return panels
}

Accordion.propTypes = {
    borderless: PropTypes.bool,
    children: PropTypes.node.isRequired,
    fixed: PropTypes.bool,
    headerBackgroundColor: PropTypes.string,
    onClick: PropTypes.func,
    openedOnMount: PropTypes.bool,
    rightComponent: PropTypes.node,
    sticked: PropTypes.bool,
}

export default Accordion
